import { BAND_HEX as CANONICAL_BAND_HEX } from '../../../utils/exceptionSeverity'

export type HealthBand = 'Good' | 'Watch' | 'Poor' | 'Critical' | 'Not Configured' | 'No Data'
export type HealthColor = 'green' | 'orange' | 'red' | 'black' | 'grey'

export interface DomainScore {
  band: HealthBand
  color: HealthColor
  score: number | null
  exception_count?: number
}

export interface OverallHealth {
  score: number | null
  band: HealthBand
  color: HealthColor
}

// Dashboard-specific ordering: used only to find the worst band across
// per-domain scores. Not shared with exception-severity resolver which
// works off thresholds, not a rank.
export const BAND_RANK: Record<HealthBand, number> = {
  Critical: 4,
  Poor: 3,
  Watch: 2,
  Good: 1,
  'No Data': 0,
  'Not Configured': 0,
}

export const BAND_COLOR: Record<HealthBand, HealthColor> = {
  Good: 'green',
  Watch: 'orange',
  Poor: 'red',
  Critical: 'black',
  'No Data': 'grey',
  'Not Configured': 'grey',
}

export const BAND_DESCRIPTION: Record<HealthBand, string> = {
  Good: 'Your data is in good shape across all areas.',
  Watch: 'A few areas are showing early warning signs.',
  Poor: 'Several areas have issues that need attention.',
  Critical: 'Critical issues are present and need urgent review.',
  'No Data': 'Health will appear once the first sync completes.',
  'Not Configured': 'Health scoring is not yet configured for this franchise.',
}

// 'No Data' isn't in the canonical severity ladder; render it grey like
// 'Not Configured' (both indicate absence of a meaningful score).
export function bandHex(band: HealthBand): string {
  if (band === 'No Data') return CANONICAL_BAND_HEX['Not Configured']
  return CANONICAL_BAND_HEX[band]
}

export function deriveOverallHealth(
  scores?: Record<string, DomainScore>,
): OverallHealth {
  if (!scores) return { score: null, band: 'No Data', color: 'grey' }

  const explicit = scores.overall
  if (explicit) {
    return {
      score: explicit.score,
      band: explicit.band,
      color: explicit.color ?? BAND_COLOR[explicit.band],
    }
  }

  const valid = Object.values(scores).filter(
    (s) => s && s.band !== 'Not Configured' && s.band !== 'No Data',
  )
  if (valid.length === 0) return { score: null, band: 'No Data', color: 'grey' }

  const numericScores = valid
    .map((s) => s.score)
    .filter((n): n is number => typeof n === 'number')
  const avgScore =
    numericScores.length > 0
      ? numericScores.reduce((a, b) => a + b, 0) / numericScores.length
      : null

  const worstBand = valid.reduce<HealthBand>((worst, curr) => {
    return BAND_RANK[curr.band] > BAND_RANK[worst] ? curr.band : worst
  }, 'Good')

  return {
    score: avgScore === null ? null : Math.round(avgScore * 10) / 10,
    band: worstBand,
    color: BAND_COLOR[worstBand],
  }
}
